/**
 * SiteBoundaryParcelLegend — the clean, boundary-style per-parcel legend.
 *
 * J7 — extracted from `SiteSidebarActiveBoundary` (the SITE BOUNDARY
 * landing-panel legend William QA'd as the desired style) so the report's
 * Site-Overview map can drop the category-grouped LAYERS legend
 * (`SiteLayerSidebar`) and use the SAME ledger instead:
 *
 *   TOTAL 1170 AC
 *   OVERLAPPING PARCELS DEDUPLICATED        (only when parcels overlap)
 *   ● Parcel 1 …………………………………… 856 ac
 *   ● Parcel 2 …………………………………… 314 ac
 *
 * The dot colour is drawn from {@link AURORA_PARCEL_COLORS}, the SAME palette
 * the map overlay paints each parcel outline with (via `parcelStrokeColors`),
 * so legend dots match the drawn boundary one-for-one.
 *
 * Pure UI / no state. Returns `null` when there are no usable parcels so the
 * caller can fall back to its own legend (honest degradation). Single-parcel
 * sites collapse to a one-line "N acres" caption, matching the landing panel.
 */
import React from 'react';
import type { SiteBoundaryParcel, SiteBoundaryAggregate } from '../../../types/siteBoundary';
import { AURORA_PARCEL_COLORS } from '../../../config/siteLayerColors';
import { formatFixed } from '../../../lib/coerceNumber';

export interface SiteBoundaryParcelLegendProps {
  /** Materialized parcels (canonical multi-parcel shape, legacy-lifted). */
  parcels: ReadonlyArray<SiteBoundaryParcel>;
  /** Aggregate across the parcels (total / overlap flag). */
  aggregate: SiteBoundaryAggregate | null;
  /**
   * Single-parcel acreage fallback. When there is exactly one parcel the
   * legend shows this one-liner (the per-parcel breakdown is redundant for a
   * single parcel). Pass the site/report `siteArea`.
   */
  singleParcelAcres?: number | null;
  /** Compact (smaller type) for the on-map figure. Default false. */
  compact?: boolean;
  className?: string;
}

export const SiteBoundaryParcelLegend: React.FC<SiteBoundaryParcelLegendProps> = ({
  parcels,
  aggregate,
  singleParcelAcres,
  compact = false,
  className,
}) => {
  const hasParcels = parcels.length > 0;
  const multiParcel = parcels.length > 1;
  // No usable parcel geometry → render nothing; the caller falls back to its
  // own legend so a layer-only map (no boundary) still shows something.
  if (!hasParcels || !aggregate) return null;

  const totalCls = compact ? 'text-[10px]' : 'type-meta';
  const rowCls = compact ? 'text-[10px]' : 'type-meta';

  return (
    <div
      data-testid="site-boundary-parcel-legend"
      className={`space-y-0.5 ${className ?? ''}`}
    >
      {!multiParcel ? (
        singleParcelAcres != null ? (
          <div className={`${rowCls} text-text-muted`}>
            {formatFixed(singleParcelAcres, 0, '–')} acres
          </div>
        ) : null
      ) : (
        <>
          <div className={`${totalCls} font-bold uppercase tracking-widest text-text-primary`}>
            Total: {formatFixed(aggregate.totalArea, 0, '–')} ac
          </div>
          {aggregate.hasOverlappingParcels && (
            <div className={`${rowCls} text-text-muted uppercase tracking-widest`}>
              Overlapping parcels deduplicated
            </div>
          )}
          {parcels.map((p, i) => {
            const c = AURORA_PARCEL_COLORS[i % AURORA_PARCEL_COLORS.length];
            return (
              <div
                key={p.id}
                data-testid={`parcel-legend-row-${p.id}`}
                className={`flex items-center gap-1.5 ${rowCls} text-text-secondary`}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: c.dot }}
                  aria-hidden="true"
                />
                <span className="truncate flex-1">{p.label ?? `Parcel ${i + 1}`}</span>
                <span className="font-mono tabular-nums">{formatFixed(p.area, 0, '–')} ac</span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};
